import {sleep} from '@benchling/js-utils';

import EntryActions from 'actions/EntryActions';
import {assert, assertAndContinue} from 'Asserts';
import {getPendingAvScanFileNames} from 'av-scan/AvScanEntryUtils';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  ReviewerType,
  ReviewLifecycleActionType,
  ReviewLifecycleTerminalState,
  ReviewStatus,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import * as metrics from 'metrics/Metrics';
import * as ReviewLifecyclesDAO from 'notebook/reviews/review-lifecycles/ReviewLifecyclesDAO';
import ReviewConstants, {
  COMMENT as REVIEW_CONSTANTS_COMMENT_ICON,
  IN_PROGRESS_FILTER,
  ReviewAction,
  ReviewSummary,
  ReviewableEntryGraphQL,
  ReviewHistoryItem,
  HistoryItemReviewStatus,
} from 'notebook/reviews/ReviewConstants';
import areAnyCellsStillComputing from 'notebook/structured-tables/util/areAnyCellsStillComputing';
import {getTablesInNoteAsTables} from 'notebook/structured-tables/util/getTablesInNote';
import * as util from 'notebook/tables/excess/Util';
import {NoteRuntimeSnapshot} from 'notebook/tables/TableStoreTransforms';
// @ts-expect-error
import EntryStore from 'stores/EntryStore';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {EntityWithTextAndSuspended, MinimalEntity} from 'ui-library/entity-input/EntityInputConstants';
import {ALL_RESULTS_QUERY, SearchResponse} from 'ui-library/entity-input/EntityInputWithSearch';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import licenses from 'user/Licenses';
import APIUtil from 'util/APIUtil';
import escapeRegExp from 'util/escapeRegExp';
import {assertNever} from 'util/Types';
import StageEntryStore from 'workflows/stage-entries/StageEntryStore';

import {
  REVIEW_COMPLETION_STATUS_TO_DISPLAY_TEXT,
  REVIEW_ACTION_TO_DISPLAY_PAST_TENSE,
} from './review-lifecycles/ReviewLifecycleConstants';

const BUTTON_STYLE_BY_ACTION = {
  [ReviewAction.REJECT]: ButtonStyle.NEGATIVE,
  [ReviewAction.SEND_FOR_REVIEW]: ButtonStyle.TERTIARY_GRAY,
  [ReviewAction.ACCEPT]: ButtonStyle.PRIMARY,
  [ReviewAction.RETRACT]: ButtonStyle.NEGATIVE,
  [ReviewAction.COMMENT]: ButtonStyle.TERTIARY_GRAY,
  // TODO(BNCH-154359): Placeholder style added only to satisfy the exhaustive
  // ReviewAction key check on actionToButtonStyle below. No production code path triggers
  // COMPLETE_CYCLE yet
  [ReviewAction.COMPLETE_CYCLE]: ButtonStyle.PRIMARY,
};

const REVIEW_LIFECYCLE_ACTION_TITLES = {
  ACCEPT: 'Accepting',
  APPROVE: 'Approving',
  COMPLETE: 'Completing',
  REVIEW: 'Reviewing',
  SELF_REVIEW: 'Self-Reviewing',
  WITNESS: 'Witnessing',
};

const PENDING_SNAPSHOT_TEXT = '(pending snapshot)';

export const SNAPSHOT_PERSIST_TIMEOUT_MS = 120000;
export const SNAPSHOT_SAVE_MIN_TIMEOUT_MS = 30000;

function canUserReviewTemplate(type: string): boolean {
  return (
    licenses.hasLicense('LICENSE_REVIEWS') &&
    ([ModelTypeConstants.STAGE_CONFIGURATION, ModelTypeConstants.TEMPLATE] as Array<string>).includes(type)
  );
}

function isTypeReviewable(type: string): boolean {
  return ([ModelTypeConstants.ENTRY, ModelTypeConstants.STAGE_ENTRY] as Array<string>).includes(type);
}

/**
 * When evaluation snapshots and/or persist eval save are enabled, ensure that a NoteRuntimeSnapshot has
 * been persisted to the DB and associated with the entry prior to submitting for review. This function
 * encapsulates the two steps required to accomplish that goal:
 * 1. Wait for the existing NoteRuntimeSnapshot to be completely resolved (or generate a new snapshot of one
 *    does not exist).
 * 2. Trigger an entry update (persist) cycle and wait for it to complete.
 *
 * If these steps cannot complete within two minutes, an exception is thrown.
 *
 * @param {string} entryId
 */
export async function ensureCurrentRuntimeSnapshotPersisted(entryId: string): Promise<void> {
  if (!(APIUtil.apiIdToType(entryId) === ModelTypeConstants.ENTRY)) {
    return;
  }

  // This should never throw; the entry should always be in the store when a user is submitting for review.
  if (!EntryStore.inStore(entryId)) {
    throw new Error('Snapshot resolution failed');
  }

  const start = new Date().getTime();
  const timeoutMs = SNAPSHOT_PERSIST_TIMEOUT_MS;

  while (true) {
    if (_isEvaluationSnapshotFinishedLoading(EntryStore.getEntry(entryId).note.noteRuntimeSnapshot)) {
      metrics.gauge('EvaluationSnapshots.awaitEntrySnapshotResolution', new Date().getTime() - start, {});
      break;
    }
    if (new Date().getTime() - start >= timeoutMs) {
      throw new Error('Snapshot query resolution timed out');
    }
    await sleep(1000);
  }

  // Kick off an entry update with `bumpVersion` set to true. If the entry does not have local edits, this
  // will not result in a new entry version. However, if there are unsaved changes (i.e. a bootstrapped
  // NoteRuntimeSnapshot), this line will cause a new entry version to be persisted with the changes.
  const saveStart = new Date().getTime();
  // Guarantee the save loop at least 30s even if snapshot resolution consumed most of the budget.
  const saveTimeoutMs = Math.max(timeoutMs - (saveStart - start), SNAPSHOT_SAVE_MIN_TIMEOUT_MS);
  EntryActions.update(entryId, {$merge: {}}, {bumpVersion: true});

  while (true) {
    if (!EntryStore.hasUnsavedChanges(entryId)) {
      metrics.gauge(
        'EvaluationSnapshots.persistEntryWithSnapshotForReview',
        new Date().getTime() - saveStart,
        {}
      );
      return;
    }
    if (new Date().getTime() - saveStart >= saveTimeoutMs) {
      throw new Error('Snapshot save resolution timed out');
    }
    await sleep(1000);
  }
}

function _isEvaluationSnapshotFinishedLoading(noteRuntimeSnapshot: NoteRuntimeSnapshot): boolean {
  if (noteRuntimeSnapshot.excessVersion == null || noteRuntimeSnapshot.evaluationSnapshot == null) {
    return false;
  }

  return !util.hasLoadingCells(noteRuntimeSnapshot.evaluationSnapshot);
}

export function getReviewFromStore(fileId: string): ReviewSummary | null {
  // TODO(T9225) Use ReviewStore
  if (EntryStore.inStore(fileId)) {
    return EntryStore.getEditorState(fileId).entry.review;
  } else if (StageEntryStore.inStore(fileId)) {
    return StageEntryStore.getEditorState(fileId).stageEntryDocument.review ?? null;
  } else {
    return null;
  }
}

export function canUserReviewType(type: string): boolean {
  return licenses.hasLicense('LICENSE_REVIEWS') && isTypeReviewable(type);
}

export function hasReviewsEnabled(type: string): boolean {
  if (type === ModelTypeConstants.TEMPLATE) {
    return BenchlingConfig.ENABLE_ELN_REVIEW_CRITERIA;
  }
  return licenses.hasLicense('LICENSE_REVIEWS') && isTypeReviewable(type);
}

export function getDefaultReviewComment(
  action: ReviewAction | ReviewLifecycleActionType,
  isForReviewLifecycleAction: boolean = false
): string {
  // Legacy auditor-based reviews will not have default review comments when ENABLE_ELN_REVIEW_COMMENTS is disabled, otherwise this will block them from sending for review.
  const isLegacyReviewWithDisabledComments =
    !isForReviewLifecycleAction && !BenchlingConfig.ENABLE_ELN_REVIEW_COMMENTS;
  if (isLegacyReviewWithDisabledComments) {
    return '';
  }
  return BenchlingConfig.ELN_REVIEW_DEFAULT_COMMENTS[action] || '';
}

export function getReviewLifecycleActionTitle(rlcAction: ReviewLifecycleActionType): string {
  return REVIEW_LIFECYCLE_ACTION_TITLES[rlcAction] || '';
}

export function actionToButtonStyle(action: ReviewAction): ButtonStyle {
  return BUTTON_STYLE_BY_ACTION[action];
}

export function getIconNameFromAction(action: ReviewAction): BenchlingIconName {
  switch (action) {
    case ReviewAction.ACCEPT:
      return ReviewStatus.ACCEPTED;
    case ReviewAction.REJECT:
      return ReviewStatus.REJECTED;
    case ReviewAction.RETRACT:
      return ReviewStatus.RETRACTED;
    case ReviewAction.SEND_FOR_REVIEW:
      return ReviewStatus.NEEDS_REVIEW;
    case ReviewAction.COMMENT:
      return REVIEW_CONSTANTS_COMMENT_ICON;
    case ReviewAction.COMPLETE_CYCLE:
      return ReviewStatus.CYCLE_COMPLETED;
    default:
      assertAndContinue(false, `Unexpected review action: ${assertNever(action)}`);
      return 'notdef';
  }
}

export function getEditWarning(status: ReviewStatus): string | null {
  switch (status) {
    case ReviewConstants.REVIEW_SNAPSHOT_IN_PROGRESS:
      return 'Editing is disabled because the entry is being snapshotted for review.';
    case ReviewConstants.ACTION_REQUIRED:
    case ReviewConstants.NEEDS_REVIEW:
      return 'Editing is disabled because the entry is in review.';
    case ReviewConstants.ACCEPTANCE_SNAPSHOT_IN_PROGRESS:
      return 'Editing is disabled because the entry is being snapshotted after review.';
    case ReviewConstants.ACCEPTED:
      return 'Editing is disabled because the entry has been reviewed.';
    default:
      throw new Error(`Unexpected review status: ${status}`);
  }
}

export function getStatusCSSClassFromReview(status?: ReviewStatus): string | null {
  // This could eventually take in the current user, and show different colors based on
  // if the current user is an entry author or not.
  switch (status) {
    case ReviewStatus.REJECTED:
    case ReviewStatus.ACTION_REQUIRED: // shares the same style as a rejected review
      return 'is-rejected';
    case ReviewStatus.REVIEW_SNAPSHOT_IN_PROGRESS:
    case ReviewStatus.NEEDS_REVIEW:
      return 'is-needsReview';
    case ReviewStatus.ACCEPTANCE_SNAPSHOT_IN_PROGRESS:
    case ReviewStatus.ACCEPTED:
      return 'is-accepted';
    case ReviewStatus.RETRACTED:
      return 'is-retracted';
    // TODO(BNCH-154359): Verify correct CSS class for CYCLE_COMPLETED once frontend UX lands.
    case ReviewStatus.CYCLE_COMPLETED:
      return 'is-accepted';
    case null:
    case undefined:
      return 'is-inProgress';
    default:
      throw new Error(`Unexpected review status: ${status}`);
  }
}

// This could eventually take in the current user, and show different colors based on if the current user is an entry author or not.
export function getStatusIcon(
  status?: ReviewStatus,
  defaultStatusIcon?: BenchlingIconName
): BenchlingIconName {
  // Uses defaultStatusIcon if available, otherwise in progress icon.
  // This is only used when review has not started or the review has been retracted
  if (!status || status === ReviewConstants.RETRACTED) {
    return defaultStatusIcon || IN_PROGRESS_FILTER;
  }
  return status;
}

// Keep in sync with benchling/notebook/reviews/models/review.py::get_status_display_text
export function getStatusTextFromReview(
  status: ReviewStatus | undefined,
  terminalState?: ReviewLifecycleTerminalState,
  defaultText?: string
): string | null {
  const acceptanceText = terminalState ? REVIEW_COMPLETION_STATUS_TO_DISPLAY_TEXT[terminalState] : 'Accepted';
  switch (status) {
    case ReviewConstants.REJECTED:
      return 'Needs revision';
    case ReviewConstants.ACTION_REQUIRED:
      return 'Action required';
    case ReviewConstants.REVIEW_SNAPSHOT_IN_PROGRESS:
      // A reviewable is not formally "sent for review" until the snapshot succeeds.
      return 'Sent for review (pending snapshot)';
    case ReviewConstants.NEEDS_REVIEW:
      return 'Needs review';
    case ReviewConstants.ACCEPTANCE_SNAPSHOT_IN_PROGRESS:
      // A reviewable is not formally "accepted" until the snapshot succeeds.
      return `${acceptanceText} (pending snapshot)`;
    case ReviewConstants.ACCEPTED:
      return acceptanceText;
    // TODO(BNCH-154359): Verify correct display text for CYCLE_COMPLETED once frontend UX lands.
    case ReviewConstants.CYCLE_COMPLETED:
      return 'Cycle completed';
    case ReviewConstants.RETRACTED:
    case null:
    case undefined:
      return defaultText ?? 'In progress';
    default:
      throw new Error(`Unexpected review status: ${status}`);
  }
}

export {isTypeReviewable, canUserReviewTemplate};

export function searchForReviewers(
  query: string,
  reviewerType: ReviewerType,
  authors: Array<MinimalEntity> | undefined,
  selectedTeamId: string | undefined,
  reviewSenderId: string | undefined,
  reviewableId: string,
  allowAuthorReviewers: boolean,
  allowSenderReviewer: boolean
): Promise<SearchResponse> {
  // TODO (BNCH-133106): Add an assertion that the reviewSenderId is not undefined when sending for review
  const filterAuthorsAndReviewSender = (
    entitySummaries: Array<EntityWithTextAndSuspended> | undefined
  ): Array<EntityWithTextAndSuspended> | undefined =>
    entitySummaries?.filter((entity) => {
      if (!allowAuthorReviewers && authors?.some((author) => author.id === entity.id)) {
        return false;
      }
      if (entity.id === reviewSenderId && !allowSenderReviewer) {
        return false;
      }
      return true;
    });

  switch (reviewerType) {
    case ReviewerType.TEAM: {
      assert(
        selectedTeamId != null,
        'Reviewer type is team but there is no selected team on the review lifecycle stage'
      );
      const teamIds = [selectedTeamId];
      return ReviewLifecyclesDAO.searchForReviewers(reviewableId, query, teamIds).then((response) => ({
        entitySummaries: filterAuthorsAndReviewSender(response.entitySummaries),
      }));
    }
    case ReviewerType.PROJECT_MEMBERS:
      return ReviewLifecyclesDAO.searchForReviewers(reviewableId, query).then((response) => ({
        entitySummaries: filterAuthorsAndReviewSender(response.entitySummaries),
      }));
    case ReviewerType.AUTHOR: {
      let authorEntities = authors;
      // if sender reviewer inclusion is not checked, filter out the review sender from authors
      if (!allowSenderReviewer) {
        authorEntities = authorEntities?.filter((author) => author.id !== reviewSenderId);
      }
      // context: query string is hard-coded to ALL_RESULTS_QUERY if it is an empty string
      //          query string needs to be hard-coded to ALL_RESULTS_QUERY for above DAO searches to work
      // only perform author search if query string is not ALL_RESULTS_QUERY
      if (query !== ALL_RESULTS_QUERY) {
        authorEntities = authorEntities?.filter((author) =>
          author.name!.match(new RegExp(escapeRegExp(query), 'i'))
        );
      }

      return Promise.resolve({
        entitySummaries: authorEntities?.map((user) => ({
          id: user.id,
          avatar_url: user.avatarUrl ?? '',
          handle: user.handle,
          name: user.name ?? '',
          joinedOn: '',
          url_identifier: '',
          email: '',
          academic: false,
          institution: null,
          isTenantAdmin: false,
          proclaimed: null,
          status: null,
          suspended: false,
          type: ModelTypeConstants.USER,
          text: '',
        })),
      });
    }
    default:
      assert(false, `Unexpected reviewerType: ${assertNever(reviewerType)}`);
  }
}

/**
 * Return whether the reviewable's status is one from which the reviewable can be sent for review.
 *
 * In other words, is `NEEDS_REVIEW` a valid subsequent state transition from the current state?
 *
 * @param reviewStatus - Ideally passed in as `review?.status`. Should be `undefined` if entry has never been
 *                       sent for review.
 */
export function canSendForReviewFromStatus(reviewStatus?: ReviewStatus): boolean {
  return reviewStatus == null || [ReviewStatus.REJECTED, ReviewStatus.RETRACTED].includes(reviewStatus);
}

export async function refreshFrozenChipVersion(entryId: string, reviewStatus: ReviewStatus): Promise<void> {
  const entryType = APIUtil.apiIdToType(entryId);
  // TODO(BNCH-104019): Support frozen chip version refresh in Worksheet Documents
  if (entryType !== ModelTypeConstants.ENTRY) {
    // Currently no support for FrozenChips in non-entries,
    // So no need to perform a refresh as no version will be generated
    return;
  }
  let loadingMessage;
  if (reviewStatus === ReviewStatus.NEEDS_REVIEW) {
    loadingMessage = 'Sending for Review';
  } else if (reviewStatus === ReviewStatus.ACCEPTED) {
    loadingMessage = 'Accepting Review';
  } else {
    loadingMessage = 'Retracting Review';
  }
  LoadingScreen.show(loadingMessage);
  await EntryActions.open(entryId);
  LoadingScreen.hide();
}

export function isReviewableEntry(data?: {__typename?: string}): data is ReviewableEntryGraphQL {
  return data?.__typename === 'EntryNode';
}

export function getComputingCellsErrorMessage(reviewableId: string): string | null {
  const note = StageEntryStore.inStore(reviewableId)
    ? StageEntryStore.getNote(reviewableId)
    : EntryStore.inStore(reviewableId)
      ? EntryStore.getNote(reviewableId)
      : null;
  const tables = getTablesInNoteAsTables(note?.noteParts ?? [], reviewableId);
  const tableNames = areAnyCellsStillComputing(tables);
  if (!tableNames) {
    return null;
  }
  return `Cannot send for review while table cells are still computing in [${tableNames.join(', ')}].`;
}

export function getPendingAvScanErrorMessage(reviewableId: string): string | null {
  if (!BenchlingConfig.ENABLE_ANTIVIRUS_SCANNING) {
    return null;
  }

  const pendingFileNames = getPendingAvScanFileNames(reviewableId);
  if (pendingFileNames.length === 0) {
    return null;
  }
  return `Please wait for malware scan to complete before sending for review. Files pending scan: [${pendingFileNames.join(', ')}].`;
}

export function getReviewBlockingErrorMessage(reviewableId: string): string | null {
  const errors: Array<string> = [];

  const computingCellsError = getComputingCellsErrorMessage(reviewableId);
  if (computingCellsError) {
    errors.push(computingCellsError);
  }

  const pendingAvScanError = getPendingAvScanErrorMessage(reviewableId);
  if (pendingAvScanError) {
    errors.push(pendingAvScanError);
  }

  if (errors.length === 0) {
    return null;
  }

  return errors.join(' • ');
}

// Used to determine whether to show the new review history UI or the old review history UI in review snapshots.
export function shouldShowNewReviewHistory(reviewHistoryItems: Array<ReviewHistoryItem>): boolean {
  // If the config flag is 0, bypass any other logic and use the old Review History UI.
  if (BenchlingConfig.ELN_EXPORT_REVIEW_HISTORY_UI_VERSION === 0) {
    return false;
  }

  if (reviewHistoryItems.length === 0) {
    return false;
  }

  for (const item of reviewHistoryItems) {
    // If the signature data is not present, or the point-in-time review history UI version was 0,
    // use the old review history UI.
    if (!item.info?.signatureData || item.info.signatureData.reviewHistoryUiVersion === 0) {
      return false;
    }
  }
  return true;
}

// Helper functions extracted from EntryExportMetadata.tsx
export function getStatusForStageAcceptance(reviewStageAction?: ReviewLifecycleActionType): string {
  assert(
    reviewStageAction != null,
    "Cannot have an acceptance review change for an entry that's in review without a review stage"
  );
  return REVIEW_ACTION_TO_DISPLAY_PAST_TENSE[reviewStageAction];
}

export function getAcceptAction(reviewStageAction?: ReviewLifecycleActionType): string {
  return reviewStageAction ? getStatusForStageAcceptance(reviewStageAction) : 'Accepted';
}

export function getNewEntryStatus(
  mostRecentStatus: HistoryItemReviewStatus | null,
  terminalCompletionState: string
): string | null {
  if (!mostRecentStatus) {
    return '';
  }
  const reviewStatusToDisplayStatus: {[K in HistoryItemReviewStatus]: string} = {
    [ReviewStatus.REVIEW_SNAPSHOT_IN_PROGRESS]: `Needs review ${PENDING_SNAPSHOT_TEXT}`,
    [ReviewStatus.NEEDS_REVIEW]: 'Needs review',
    [ReviewStatus.ACCEPTED]: terminalCompletionState,
    [ReviewStatus.ACCEPTANCE_SNAPSHOT_IN_PROGRESS]: `${terminalCompletionState} ${PENDING_SNAPSHOT_TEXT}`,
    [ReviewStatus.REJECTED]: 'Needs revision',
    [ReviewStatus.RETRACTED]: 'In progress',
    // TODO(BNCH-154359): Placeholder display string added only to satisfy the exhaustive mapped
    // type below. No production code path persists a CYCLE_COMPLETED review yet
    [ReviewStatus.CYCLE_COMPLETED]: 'Cycle completed',
  };
  return reviewStatusToDisplayStatus[mostRecentStatus];
}

export function generateSentForReviewText(reviewLifecycleName?: string): string {
  const maybeWithRlcName = reviewLifecycleName ? ` - ${reviewLifecycleName}` : '';
  return `Sent for review${maybeWithRlcName}`;
}

export function wasEntryMostRecentlyAcceptedOrInReview(
  history: Array<ReviewHistoryItem>,
  lastIndex: number
): ReviewStatus.ACCEPTED | ReviewStatus.NEEDS_REVIEW {
  assert(history.length > 0, 'No history for entry found');
  let currIndex = lastIndex;
  while (currIndex >= 0) {
    const currNewStatus = history[currIndex].info.new_status;
    if (currNewStatus === ReviewStatus.ACCEPTED || currNewStatus === ReviewStatus.NEEDS_REVIEW) {
      return currNewStatus;
    }
    currIndex -= 1;
  }
  assert(false, 'Entry with review was somehow never accepted nor sent for review.');
}

/**
 * For a given history item, provide the values for the action and new status columns.
 *
 * When includeNewStatusColumn is set to false, newStatus will be null, and action will be much more
 * descriptive. This should only be set to false for legacy reviews.
 */
export function actionAndStatusText(
  history: Array<ReviewHistoryItem>,
  currentItemIndex: number,
  includeNewEntryStatusColumn: boolean,
  includeReviewLifecycleNameInSentForReview: boolean = true
): {action: string; newEntryStatus: string | null} {
  const {
    info: {reviewAction, wasCreatedBySnapshot, new_status: newStatus},
    reviewLifecycleInfo,
  } = history[currentItemIndex];

  const stageAction = reviewLifecycleInfo?.reviewStageInfo?.action;
  // This was a hack to get the review lifecycle name to show up in the "Sent for review" action.
  // The new Review History UI has a separate column for the review lifecycle name so we are adding the
  // option to not include it. This is only used for the "Sent for review" action.
  const reviewLifecycleName = includeReviewLifecycleNameInSentForReview ? reviewLifecycleInfo?.name : '';
  const terminalState = reviewLifecycleInfo?.terminalState;

  const terminalCompletionDisplayText =
    // Default to accepted in the non-RLC case.
    REVIEW_COMPLETION_STATUS_TO_DISPLAY_TEXT[terminalState ?? ReviewLifecycleTerminalState.ACCEPTED];

  const newEntryStatus = includeNewEntryStatusColumn
    ? getNewEntryStatus(newStatus, terminalCompletionDisplayText)
    : null;

  // Handle comment case first since comments are allowed during almost every review state of the entry.
  // This also handles the newStatus == null case as newStatus is not populated for comment review actions.
  if (reviewAction === ReviewAction.COMMENT) {
    return {action: 'Added comment', newEntryStatus};
  }

  let action = '';
  switch (newStatus) {
    case 'REVIEW_SNAPSHOT_IN_PROGRESS':
      action = includeNewEntryStatusColumn
        ? generateSentForReviewText(reviewLifecycleName)
        : `${generateSentForReviewText(reviewLifecycleName)} ${PENDING_SNAPSHOT_TEXT}`;
      break;
    case 'NEEDS_REVIEW':
      if (wasCreatedBySnapshot) {
        action = includeNewEntryStatusColumn ? 'Snapshot completed' : 'Ready for review (snapshot completed)';
      } else if (reviewAction === ReviewAction.ACCEPT) {
        assert(
          stageAction != null,
          'Acceptance can only lead to NEEDS_REVIEW status if associated with a review stage'
        );
        action = REVIEW_ACTION_TO_DISPLAY_PAST_TENSE[stageAction];
      } else {
        action = generateSentForReviewText(reviewLifecycleName);
      }
      break;

    case 'ACCEPTANCE_SNAPSHOT_IN_PROGRESS':
      action = includeNewEntryStatusColumn
        ? getAcceptAction(stageAction)
        : `${getAcceptAction(stageAction)} ${PENDING_SNAPSHOT_TEXT}`;
      break;
    case 'ACCEPTED': {
      action = getAcceptAction(stageAction);
      if (wasCreatedBySnapshot) {
        action = includeNewEntryStatusColumn ? 'Snapshot completed' : `${action} (snapshot completed)`;
      }
      break;
    }
    case 'REJECTED':
      action = 'Rejected';
      break;
    case 'RETRACTED':
      if (reviewLifecycleInfo) {
        action =
          wasEntryMostRecentlyAcceptedOrInReview(history, currentItemIndex) === ReviewStatus.ACCEPTED
            ? 'Reopened entry'
            : 'Canceled review';
      } else {
        action = 'Retracted';
      }
      break;
    default:
      throw Error(`Unexpected review status: ${newStatus}`);
  }
  return {
    action,
    newEntryStatus,
  };
}
